/**
 * Repeatable, anchored app notice — the FTUE coachmark without the FTUE engine.
 *
 * Mounted once in the app layout (next to `FtueHost`) so a notice survives the
 * route change that raises it: Job Agent bounces a user with no resume to
 * /resume and the tip lands on the "add your first resume" section there.
 *
 * It renders the shared `Coachmark` from a synthetic `ActiveFtueStep`, so the
 * chrome, placement and motion are identical to a real Pelgo Tip. What it does
 * NOT do is go through the engine, and that is the point:
 *  - FTUE steps are account-persisted and fire once ever; a notice must fire
 *    EVERY time the user repeats the action that raised it.
 *  - `spotlight: false` keeps it non-blocking — no scrim, no interaction guard,
 *    no focus trap (the FtueHost scrim only exists for engine-owned steps).
 *
 * Resolution follows the engine's own rules: while the anchor is off-screen the
 * notice waits (renders nothing) rather than showing detached copy, and a real
 * FTUE coachmark always wins — it is the user's primary journey and can share
 * this anchor, so two cards must never stack on it. Once visible, the notice
 * auto-dismisses after 5s (the progress bar is the coachmark's own
 * `autoCompleteMs` affordance) and "Ok" dismisses it immediately.
 */

import { Coachmark, getElementLayer, useFtue, useFtueAnchorElement } from '@/ftue'
import type { ActiveFtueStep } from '@/ftue'
import { useAppNoticeStore } from '@/stores/app-notice-store'
import { useEffect } from 'react'

const AUTO_DISMISS_MS = 5000

const NOTICE_STEP_ID = 'app-notice'
const NOTICE_FLOW_ID = 'app-notice'

export function AppNoticeCoachmark() {
  const notice = useAppNoticeStore((state) => state.notice)
  const clearNotice = useAppNoticeStore((state) => state.clearNotice)
  const element = useFtueAnchorElement(notice?.anchorId)
  const ftueActive = useFtue()

  // A real FTUE step owns the renderer while it is on screen.
  const ftueCoachmarkShowing = ftueActive?.surface === 'coachmark' && ftueActive.element != null
  const visible = Boolean(notice) && element != null && !ftueCoachmarkShowing

  // Auto-dismiss, timed from when the notice actually becomes visible — a notice
  // still waiting on its anchor must not expire before it has been seen.
  useEffect(() => {
    if (!visible) return
    const timer = window.setTimeout(clearNotice, AUTO_DISMISS_MS)
    return () => window.clearTimeout(timer)
  }, [visible, clearNotice])

  if (!notice || !element || ftueCoachmarkShowing) return null

  const active: ActiveFtueStep = {
    step: {
      id: NOTICE_STEP_ID,
      flow: NOTICE_FLOW_ID,
      order: 1,
      trigger: { type: 'immediate' },
      anchorId: notice.anchorId,
      presentation: 'coachmark',
      placement: 'bottom',
      // Non-blocking: the whole point is to send the user to the section it
      // points at, so its controls must stay clickable.
      spotlight: false,
      allowAnchorInteraction: true,
      // Renders the coachmark's auto-dismiss progress bar; the timer above owns
      // the actual dismissal (the engine's host runs it for engine-owned steps).
      autoCompleteMs: AUTO_DISMISS_MS,
      content: { body: notice.body, dismissLabel: 'Ok' },
    },
    surface: 'coachmark',
    element,
    layer: getElementLayer(element),
    // Single concept: keeps the shared renderer in its "Ok" mode with no
    // Next/Back/Skip controls and no step counter.
    index: 0,
    total: 1,
    fallbackReason: null,
    onComplete: clearNotice,
    onDismiss: clearNotice,
    onSkipAll: clearNotice,
    onNext: clearNotice,
    onBack: () => {
      // Single-step surface: there is nothing to go back to.
    },
    onCtaNavigate: clearNotice,
  }

  return <Coachmark active={active} />
}
